import {
  normalizeSearchScope,
  type SearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import type {
  EventDispatchResult,
  Mediator,
  MediatorTransitionResult,
  TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { createMediator } from "@/shared/lib/componentHierarchy";

export interface SearchDraftState {
  readonly sourceQuery: string;
  readonly value: string;
}

export interface SearchPageState {
  readonly draft: SearchDraftState;
  readonly isDesktop: boolean;
  readonly isSettingsOpen: boolean;
}

export type SearchPageEvent =
  | {
      readonly type: "search/draft-changed";
      readonly sourceQuery: string;
      readonly value: string;
    }
  | {
      readonly type: "search/query-synchronized";
      readonly query: string;
      readonly scope: SearchScope;
    }
  | {
      readonly type: "search/submission-requested";
      readonly value: string;
      readonly scope: SearchScope;
    }
  | {
      readonly type: "search/scope-change-requested";
      readonly query: string;
      readonly scope: SearchScope;
    }
  | { readonly type: "search/retry-requested" }
  | { readonly type: "search/sidebar-toggle-requested" }
  | {
      readonly type: "search/scroll-requested";
      readonly edge: "top" | "bottom";
    }
  | {
      readonly type: "search/settings-link-clicked";
      readonly hasModifier: boolean;
    }
  | { readonly type: "search/settings-close-requested" }
  | {
      readonly type: "search/viewport-synchronized";
      readonly isDesktop: boolean;
    };

export type SearchPageEffect =
  | {
      readonly type: "sync-catalog-search";
      readonly query: string;
      readonly scope: SearchScope;
    }
  | {
      readonly type: "navigate-search";
      readonly query: string | undefined;
      readonly scope: "op" | undefined;
    }
  | { readonly type: "retry" }
  | { readonly type: "toggle-sidebar" }
  | { readonly type: "scroll"; readonly edge: "top" | "bottom" };

export type SearchPageEventNotifier = (
  event: SearchPageEvent,
) => EventDispatchResult<TypedEvent>["status"];

export interface SearchPageMediatorOptions {
  readonly initialQuery: string;
  readonly isDesktop: boolean;
  readonly runEffect?: (effect: SearchPageEffect) => void;
}

export function isSearchPageEvent(event: TypedEvent): event is SearchPageEvent {
  switch (event.type) {
    case "search/draft-changed":
      return (
        hasStringField(event, "sourceQuery") && hasStringField(event, "value")
      );
    case "search/query-synchronized":
      return hasStringField(event, "query") && hasSearchScope(event);
    case "search/submission-requested":
      return hasStringField(event, "value") && hasSearchScope(event);
    case "search/scope-change-requested":
      return hasStringField(event, "query") && hasSearchScope(event);
    case "search/retry-requested":
    case "search/sidebar-toggle-requested":
    case "search/settings-close-requested":
      return true;
    case "search/scroll-requested":
      return isScrollEdge((event as Record<string, unknown>).edge);
    case "search/settings-link-clicked":
      return hasBooleanField(event, "hasModifier");
    case "search/viewport-synchronized":
      return hasBooleanField(event, "isDesktop");
    default:
      return false;
  }
}

export function transitionSearchPage(
  state: SearchPageState,
  event: SearchPageEvent,
): MediatorTransitionResult<SearchPageState, SearchPageEffect> {
  switch (event.type) {
    case "search/draft-changed":
      return {
        status: "handled",
        state: {
          ...state,
          draft: {
            sourceQuery: event.sourceQuery,
            value: event.value,
          },
        },
      };
    case "search/query-synchronized":
      return {
        status: "handled",
        state:
          state.draft.sourceQuery === event.query
            ? state
            : {
                ...state,
                draft: { sourceQuery: event.query, value: event.query },
              },
        effects: [
          {
            type: "sync-catalog-search",
            query: event.query,
            scope: event.scope,
          },
        ],
      };
    case "search/submission-requested":
      return {
        status: "handled",
        state,
        effects: [
          {
            type: "navigate-search",
            query: event.value.trim(),
            scope: serializeSearchScope(event.scope),
          },
        ],
      };
    case "search/scope-change-requested":
      return {
        status: "handled",
        state,
        effects: [
          {
            type: "navigate-search",
            query: event.query || undefined,
            scope: serializeSearchScope(event.scope),
          },
        ],
      };
    case "search/retry-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "retry" }],
      };
    case "search/sidebar-toggle-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "toggle-sidebar" }],
      };
    case "search/scroll-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "scroll", edge: event.edge }],
      };
    case "search/settings-link-clicked":
      if (event.hasModifier) return { status: "unhandled" };
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: true },
      };
    case "search/settings-close-requested":
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: false },
      };
    case "search/viewport-synchronized":
      return {
        status: "handled",
        state: {
          ...state,
          isDesktop: event.isDesktop,
          isSettingsOpen: event.isDesktop ? state.isSettingsOpen : false,
        },
      };
  }
}

export function createSearchPageMediator(
  options: SearchPageMediatorOptions,
): Mediator<SearchPageState, SearchPageEvent, SearchPageEffect> {
  return createMediator<SearchPageState, SearchPageEvent, SearchPageEffect>(
    {
      draft: {
        sourceQuery: options.initialQuery,
        value: options.initialQuery,
      },
      isDesktop: options.isDesktop,
      isSettingsOpen: false,
    },
    transitionSearchPage,
    { runEffect: options.runEffect },
  );
}

function hasStringField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "string";
}

function hasBooleanField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "boolean";
}

function hasSearchScope(
  event: TypedEvent,
): event is TypedEvent & { readonly scope: SearchScope } {
  return (
    normalizeSearchScope((event as Record<string, unknown>).scope) ===
    (event as Record<string, unknown>).scope
  );
}

function isScrollEdge(value: unknown): value is "top" | "bottom" {
  return value === "top" || value === "bottom";
}
